<script lang="ts">
	/**
	 * The confirm step of a plan change (Design §9.3, §14).
	 *
	 * `ProrationPreview` is a **discriminated** shape and this component is the reason it is one. A
	 * downgrade takes no payment, so an "amount to be charged" rendering has nothing true to say
	 * about it — while the downgrade confirm step is exactly where a customer most needs the
	 * sentence "you keep Studio until 14 September, then €9". Both arms are rendered here, and
	 * neither borrows the other's wording.
	 *
	 * Every figure is formatted from the whole `Currency` row behind the code the server sent, via
	 * `formatAmount`. When no such row is configured the amount is reported as unavailable rather
	 * than guessed: ÷100 is right for EUR and a hundred times wrong for JPY, and it is wrong
	 * silently — the confirm step still shows something that looks exactly like money.
	 */

	import EmptyState from './EmptyState.svelte';
	import { formatAmount } from '../stores/storefront';
	import { formatCalendarDate } from '../Utilities';
	import type { ProrationPreview } from '../AppTypes';

	export let preview: ProrationPreview | null = null;
	/** What the customer is on now — the subject of the `scheduled` arm's sentence. */
	export let currentProductName: string = 'your current plan';
	/** What they are moving to. */
	export let targetProductName: string = 'the new plan';
	export let loading: boolean = false;
	/** Server message. The preview endpoint refuses for reasons worth reading. */
	export let error: string = '';

	/** Formatted figure, or null when the currency row is missing and a guess would be a lie. */
	function money(amountMinor: number | null | undefined, currency: string): string | null {
		return $formatAmount(amountMinor, currency);
	}

	$: unavailableCurrency = preview === null ? '' : preview.currency.toUpperCase();
</script>

{#if loading}
	<EmptyState variant="loading" compact title="Working out what this change costs…" />
{:else if error.length > 0}
	<EmptyState variant="error" title="We could not work out what this change costs." description={error} />
{:else if preview !== null && preview.kind === 'immediate'}
	<div class="widget bg-depth space-y-2">
		<div class="card-label">Due now</div>
		<p class="text-sm text-default">
			Moving to {targetProductName} takes effect straight away, and the rest of this period is
			charged at the new rate.
		</p>

		<dl class="text-sm space-y-1">
			<div class="flex justify-between gap-4">
				<dt class="text-default">Rest of this period</dt>
				<dd>{money(preview.chargeNow, preview.currency) ?? `Amount unavailable (${unavailableCurrency})`}</dd>
			</div>
			<div class="flex justify-between gap-4">
				<dt class="text-default">Credit for unused time on {currentProductName}</dt>
				<dd>
					{#if money(preview.credit, preview.currency) === null}
						Amount unavailable ({unavailableCurrency})
					{:else}
						−{money(preview.credit, preview.currency)}
					{/if}
				</dd>
			</div>
			<div class="flex justify-between gap-4">
				<dt class="text-default">Tax</dt>
				<dd>{money(preview.tax, preview.currency) ?? `Amount unavailable (${unavailableCurrency})`}</dd>
			</div>
			<div class="flex justify-between gap-4 font-semibold border-t border-gray-100 dark:border-zinc-700 pt-1">
				<dt>Charged now</dt>
				<dd>{money(preview.total, preview.currency) ?? `Amount unavailable (${unavailableCurrency})`}</dd>
			</div>

			<!--
				What the plan settles to. A proration total is a one-off arrived at from credits and
				part-periods, and on an upgrade it is usually far *smaller* than the recurring price
				because the credit for the old tier eats most of it. Left off, the "charged now"
				figure reads as the new price of the plan — which is the number the customer
				remembers, and the one they dispute when the first full invoice arrives.
			-->
			<!--
				`!= null` on purpose: a payload from a server that predates this field carries
				`undefined`, and `undefined !== null` is true — which renders the row with nothing
				in it and takes the whole preview down.
			-->
			{#if preview.nextInvoiceAmount != null}
				<div class="flex justify-between gap-4 border-t border-gray-100 dark:border-zinc-700 pt-1">
					<dt class="text-default">
						Then every period{preview.nextInvoiceAt != null
							? `, from ${formatCalendarDate(preview.nextInvoiceAt)}`
							: ''}
					</dt>
					<dd>
						{money(preview.nextInvoiceAmount, preview.currency) ??
							`Amount unavailable (${unavailableCurrency})`}
					</dd>
				</div>
			{/if}
		</dl>

		{#if money(preview.total, preview.currency) === null}
			<!-- The store prices in a currency this client was not told how to format. Saying so is
			     the only honest option: the alternative is a confirm step quoting a wrong figure. -->
			<p class="text-xs text-red-600 dark:text-red-500">
				We cannot display these amounts because {unavailableCurrency} is not configured here. The
				exact figures are on the invoice.
			</p>
		{/if}
	</div>
{:else if preview !== null}
	<div class="widget bg-depth space-y-2">
		<div class="card-label">Scheduled</div>
		<!--
			The whole reason the shape is discriminated. Nothing is charged for a downgrade, so this
			arm carries no "due now" figure at all — only what the customer keeps, until when, and
			what they pay after that.
		-->
		<p class="text-sm">
			You keep {currentProductName} until {formatCalendarDate(preview.effectiveAt)}, then move to
			{targetProductName}
			{#if money(preview.nextInvoiceAmount, preview.currency) !== null}
				at {money(preview.nextInvoiceAmount, preview.currency)} on your next invoice.
			{:else}
				at the rate shown on your next invoice.
			{/if}
		</p>
		<p class="text-xs text-default">
			Nothing is taken for this change, and you lose nothing you have already paid for.
		</p>

		{#if money(preview.nextInvoiceAmount, preview.currency) === null}
			<p class="text-xs text-red-600 dark:text-red-500">
				We cannot display the amount because {unavailableCurrency} is not configured here.
			</p>
		{/if}
	</div>
{/if}
